import React, { Fragment, useEffect, useState } from "react"

import { DialogContent, DialogTitle, Paper } from "@mui/material"
import { makeStyles } from "@mui/styles"
import { useGridApiContext } from "@mui/x-data-grid-pro"
import { defaultTo } from "ramda"

import { getLocalStorageValues, setLocalStorageValues } from "./lib"
import index18n from "../../../lib/i18n.es6"
import { LabeledSwitch } from "../../atoms"
import { Button } from "../../molecules/button"

export const useStyles = makeStyles(({ typography, withPalette }) => ({
  contentContainer: {
    "&[class*='MuiDialogContent']": {
      backgroundColor: "transparent",
      overflow: "visible",
      padding: 0,
    },
    color: "#554854",
    fontSize: typography.pxToRem(14),
    fontWeight: "bold",
  },
  counterContainer: {
    background: "#6B546A",
    borderRadius: "50%",
    color: "#F2F2F5",
    fontSize: "12px !important",
    fontWeight: "600 !important",
    height: "20px",
    lineHeight: "20px",
    pointerEvents: "none",
    textAlign: "center",
    width: "20px",
  },
  dialogTitle: {
    "&[class*='MuiDialogTitle']": {
      color: "#554854",
      fontWeight: 700,
      lineHeight: "28px",
      marginBottom: "16px",
      padding: 0,
    },
  },
  filterButtonContainer: {
    "& [class*='MuiButton-outlined']": {
      "&:hover": {
        borderColor: withPalette("background1"),
      },
      borderColor: withPalette("background1"),
    },
  },
  filterPopover: {
    "& [class*='MuiDialogContent']": {
      overflow: "visible",
    },
    border: "1px solid #ECECEC",
    boxShadow: "none",
  },
  paper: {
    height: "auto",
    padding: "32px",
    width: "430px",
  },
  resetCustomization: {
    "&[class*='MuiButtonBase-root']": {
      marginTop: "24px",
    },
  },
  switchContainer: {
    "& > div": {
      color: "#333333",
      fontWeight: "normal",
      textTransform: "capitalize",
    },
    width: "60%",
  },
}))

const MemoLabeledSwitch = ({
  field,
  getLocalStorageValues,
  headerName,
  index,
  isLocked,
  setColumnVisibility,
  setHeaderTooltip,
  setLocalStorageValues,
  tableId,
}) => {
  const settings = getLocalStorageValues(tableId, "visibleColumns")

  const onChange = (event) => {
    event.stopPropagation()
    const newValues = {
      ...settings,
      [field]: !settings[field],
    }

    setLocalStorageValues(tableId, "visibleColumns", newValues)
    setHeaderTooltip(Object.values(newValues || {})?.filter((item) => item)?.length >= 10)
    setColumnVisibility(field, !settings[field])
  }

  return (
    <LabeledSwitch
      label={headerName || field}
      disabled={isLocked || index === 0}
      checked={defaultTo(false, settings[field])}
      onChange={onChange}
    />
  )
}

export const CustomizationDialog = ({ setHeaderTooltip, setTableState, tableId, tableState }) => {
  const { customizationSettings, defaultColumns, defaultOrder } = tableState
  const { contentContainer, dialogTitle, paper, resetCustomization, switchContainer } = useStyles()
  const [customizableColumns, setCustomizableColumns] = useState([])
  const {
    current: { setColumnVisibility },
  } = useGridApiContext()

  useEffect(() => {
    const order = getLocalStorageValues(tableId, "order")
    const columns = customizationSettings
      .filter((item) => !item.isSettings && item)
      .sort((a, b) => order[a.field] - order[b.field])

    setCustomizableColumns(columns)
  }, [customizationSettings])

  const resetCustomizationHandler = () => {
    const defaultValues = customizationSettings.reduce((accum, item) => {
      return {
        ...accum,
        [item.field]: !!item.isVisible,
      }
    }, {})

    setLocalStorageValues(tableId, "visibleColumns", defaultValues)
    setLocalStorageValues(tableId, "order", defaultOrder)
    setTableState({
      ...tableState,
      dataColumns: [
        ...defaultColumns.map((item) => {
          delete item.index

          return item
        }),
      ],
    })
    Object.keys(defaultValues).forEach((item) => {
      setColumnVisibility(item, defaultValues[item])
    })
  }

  const getContent = () => (
    <div className={switchContainer}>
      {customizableColumns.map(({ field, headerName, isLocked }, index) => (
        <MemoLabeledSwitch
          key={index}
          {...{
            field,
            getLocalStorageValues,
            headerName,
            index,
            isLocked,
            setColumnVisibility,
            setHeaderTooltip,
            setLocalStorageValues,
            tableId,
          }}
        />
      ))}
    </div>
  )

  return (
    <Paper classes={{ root: paper }}>
      <DialogTitle classes={{ root: dialogTitle }}>
        {index18n("General:CustomizeTable")}
      </DialogTitle>

      <DialogContent classes={{ root: contentContainer }}>
        <Fragment>
          <div style={{ paddingBottom: "20px" }}>{index18n("General:CustomizeDialogContent")}</div>
          {getContent()}
          <Button
            label={index18n("General:ResetSettings")}
            title={index18n("General:ResetSettings")}
            variant={"outlined"}
            className={resetCustomization}
            onClick={resetCustomizationHandler}
          />
        </Fragment>
      </DialogContent>
    </Paper>
  )
}
